import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Search, Trash2, Edit2, Plus, Check, UploadCloud, FileSpreadsheet, Download, CheckCircle2, Loader2, X, MapPin, ArrowLeft, AlertTriangle, Users } from "lucide-react";
import ProviderLayout from "@/layouts/ProviderLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogFooter } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  useListCptCodes, useCreateCptCode, useUpdateCptCode, useDeleteCptCode,
  useClearAllCptCodes, getListCptCodesQueryKey, useListMasterCptCodes,
  useGetLocalities, useGetProvider, useListBundles, useGetCptCategories,
  useGetCptSubcategories, useListGroupInvitations, useGetLinkedProviders, API_BASE
} from "@/lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Label } from "@/components/ui/label";
import { ListPagination } from "@/components/ui/list-pagination";
import { useLocation } from "wouter";

const MODAL_ITEMS_PER_PAGE = 12;

export default function CptCodes() {
  const { data: fetchedCptCategories } = useGetCptCategories();
  const { data: fetchedCptSubcategories } = useGetCptSubcategories();
  const [location, setLocation] = useLocation();
  const isAddRoute = location === "/provider/cpt-codes/add";
  const [isAddMode, setIsAddMode] = useState(false);

  const showAddPage = isAddRoute || isAddMode;

  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    try {
      const u = localStorage.getItem("user");
      if (u) {
        setUser(JSON.parse(u));
      }
    } catch (e) { }

    const fetchMe = async () => {
      try {
        const storedUser = localStorage.getItem("user");
        let token = "";
        if (storedUser) {
          try { token = JSON.parse(storedUser).token || ""; } catch { }
        }
        if (!token) token = localStorage.getItem("token") || "";

        if (token) {
          const res = await fetch(`${API_BASE}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const json = await res.json();
          if (res.ok && json.success && json.data) {
            setUser((prev: any) => ({ ...prev, ...json.data }));
          }
        }
      } catch (e) { }
    };

    fetchMe();
  }, []);

  const providerMedicareRate = useMemo(() => {
    if (user?.provider?.medicareCharge) {
      const val = parseFloat(String(user.provider.medicareCharge).replace('%', ''));
      if (!isNaN(val)) return val;
    }
    return 100; // Fallback
  }, [user]);



  // Main View Filters
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [subcategory, setSubcategory] = useState("all");
  const [codeType, setCodeType] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedProviderId, setSelectedProviderId] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal State & Filters
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [modalSearch, setModalSearch] = useState("");
  const [modalCategory, setModalCategory] = useState("all");
  const [modalCodeType, setModalCodeType] = useState("all");
  const [modalSubcategory, setModalSubcategory] = useState("all");
  const [modalPage, setModalPage] = useState(1);

  // Bulk Selection State
  const [selectedModalCodes, setSelectedModalCodes] = useState<Set<string>>(new Set());
  const [batchPercentage, setBatchPercentage] = useState<string>("100");
  const [individualPercentages, setIndividualPercentages] = useState<Record<string, string>>({});
  const [manuallyEditedCodes, setManuallyEditedCodes] = useState<Set<string>>(new Set());
  const [isSubmittingBulk, setIsSubmittingBulk] = useState(false);

  const { data: teamInvitations } = useListGroupInvitations();

  const [editData, setEditData] = useState<{ master: any, provider: any } | null>(null);
  const [editPercentage, setEditPercentage] = useState<string>("100");
  const [editPrice, setEditPrice] = useState<string>("0");
  const [editStatus, setEditStatus] = useState<string>("active");
  const [editLocationId, setEditLocationId] = useState<string>("all");
  const [editProviderId, setEditProviderId] = useState<string>("all");
  const [editLocationRates, setEditLocationRates] = useState<Record<string, { pct: string; price: string }>>({});

  const updateModalRateFields = (locId: string, provId: string, ratesMap: Record<string, { pct: string; price: string }>) => {
    const key = `${locId}_${provId}`;
    const existingRate = ratesMap[key] || ratesMap[`${locId}_all`] || ratesMap[`all_${provId}`] || ratesMap["all_all"];
    const base = editData?.master?.standardPrice || 0;
    if (existingRate) {
      setEditPercentage(existingRate.pct);
      setEditPrice(existingRate.price);
    } else {
      const targetLoc = providerLocations.find(l => String(l.id) === String(locId)) || selectedLocation;
      const targetLocality = targetLoc?.locality || selectedLocality;
      const codeTypeName = (editData?.master?.codeType?.name || (typeof editData?.master?.codeType === "string" ? editData?.master?.codeType : "") || "").trim().toUpperCase();

      let locPct = providerMedicareRate || 100;

      // If a specific provider is selected, try to use their invitation categoryRates first
      if (provId && provId !== "all") {
        const selProv = practiceProviders.find(p => p.id === provId);
        const invRates = selProv?.categoryRates || {};
        const invPct = selProv?.percentage;
        // Try per-location rate from invitation, then global invitation rate
        const locRate = invRates[locId] ?? invRates["all"] ?? null;
        const codeTypeRate = locRate?.[codeTypeName] ?? locRate ?? null;
        if (typeof codeTypeRate === "number" && !isNaN(codeTypeRate)) {
          locPct = codeTypeRate;
        } else if (typeof invPct === "number" && !isNaN(invPct)) {
          locPct = invPct;
        }
      } else {
        // Fall back to group owner's categoryRates
        const ratesObj = effectiveUser?.provider?.categoryRates || effectiveUser?.categoryRates || user?.provider?.categoryRates || user?.categoryRates;
        if (ratesObj && typeof ratesObj === "object") {
          const foundRate = getRateForSelectedLocality(ratesObj, targetLoc, targetLocality, codeTypeName, editData?.master?.codeTypeId);
          if (foundRate !== null && !isNaN(foundRate)) {
            locPct = foundRate;
          }
        }
      }

      const calcPrice = base > 0 ? (base * (locPct / 100)).toFixed(2) : "0.00";
      setEditPercentage(locPct.toString());
      setEditPrice(calcPrice);
    }
  };

  const handleEditLocationChange = (locId: string) => {
    setEditLocationId(locId);
    updateModalRateFields(locId, editProviderId, editLocationRates);
  };

  const handleEditProviderChange = (provId: string) => {
    setEditProviderId(provId);
    updateModalRateFields(editLocationId, provId, editLocationRates);
  };

  const handleEditPercentageChange = (pctVal: string) => {
    setEditPercentage(pctVal);
    const pct = parseFloat(pctVal);
    const base = editData?.master?.standardPrice || 0;
    let calcPrice = editPrice;
    if (!isNaN(pct) && base > 0) {
      calcPrice = (base * (pct / 100)).toFixed(2);
      setEditPrice(calcPrice);
    }
    const key = `${editLocationId}_${editProviderId}`;
    setEditLocationRates(prev => ({
      ...prev,
      [key]: { pct: pctVal, price: calcPrice }
    }));
  };

  const handleEditPriceChange = (priceVal: string) => {
    setEditPrice(priceVal);
    const price = parseFloat(priceVal);
    const base = editData?.master?.standardPrice || 0;
    let calcPct = editPercentage;
    if (!isNaN(price) && base > 0) {
      calcPct = Math.round((price / base) * 100).toString();
      setEditPercentage(calcPct);
    }
    const key = `${editLocationId}_${editProviderId}`;
    setEditLocationRates(prev => ({
      ...prev,
      [key]: { pct: calcPct, price: priceVal }
    }));
  };

  const [deleteConfirmData, setDeleteConfirmData] = useState<{ id: string, code: string, usedInBundles?: string[] } | null>(null);

  // Excel Bulk Import State
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
  const [selectedExcelFile, setSelectedExcelFile] = useState<File | null>(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [excelSummary, setExcelSummary] = useState<any>(null);

  const handleExcelUpload = async () => {
    if (!selectedExcelFile) return toast({ title: "Select a file", description: "Please choose an Excel file to upload", variant: "destructive" });
    setUploadingExcel(true);
    setExcelSummary(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedExcelFile);

      const userStored = localStorage.getItem("user");
      let token = "";
      if (userStored) {
        try { token = JSON.parse(userStored).token || ""; } catch { }
      }

      const res = await fetch(`${API_BASE}/cpt/provider/bulk-excel`, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      const json = await res.json();
      if (res.ok && json.success) {
        setExcelSummary(json.summary);
        queryClient.invalidateQueries({ queryKey: getListCptCodesQueryKey() });
        toast({ title: "Import Successful", description: `Processed ${json.summary.totalRows} CPT codes from Excel.` });
      } else {
        toast({ title: "Import Failed", description: json.error || "Failed to process Excel file", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Network error", variant: "destructive" });
    } finally {
      setUploadingExcel(false);
    }
  };
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: linkedProvidersData } = useGetLinkedProviders();
  const { data: codesData, isLoading: codesLoading } = useListCptCodes({
    limit: 5000,
    ...(selectedProviderId && selectedProviderId !== "all" ? { providerId: selectedProviderId } : {})
  });
  const { data: masterData, isLoading: masterLoading } = useListMasterCptCodes({ limit: 5000 });
  const { data: localitiesData = [] } = useGetLocalities();

  const codes = useMemo(() => codesData?.items || [], [codesData?.items]);
  const rawMasterCodes = useMemo(() => masterData?.items || [], [masterData?.items]);

  const DEFAULT_MASTER_CATEGORIES = [
    "Anesthesia",
    "Dentistry",
    "Diagnostic Cardiology",
    "Evaluation & Management",
    "General",
    "Medicine",
    "Pathology",
    "Physical Therapy",
    "Radiology",
    "Surgery"
  ];

  const categories: string[] = useMemo(() => {
    const set = new Set<string>(DEFAULT_MASTER_CATEGORIES);
    if (Array.isArray(fetchedCptCategories)) {
      fetchedCptCategories.forEach((cat: any) => {
        const name = typeof cat === 'string' ? cat : String(cat?.name || cat?.id || '');
        if (name && name.trim()) set.add(name.trim());
      });
    }
    if (Array.isArray(rawMasterCodes)) {
      rawMasterCodes.forEach((m: any) => {
        if (m.category && typeof m.category === 'string' && m.category.trim()) set.add(m.category.trim());
      });
    }
    if (Array.isArray(codes)) {
      codes.forEach((c: any) => {
        if (c.category && typeof c.category === 'string' && c.category.trim()) set.add(c.category.trim());
      });
    }
    return Array.from(set).sort();
  }, [fetchedCptCategories, rawMasterCodes, codes]);

  const DEFAULT_CPT_CATEGORY_SUBCATEGORIES_MAP: Record<string, string[]> = {
    "Evaluation & Management": [
      "Office & Outpatient Visits",
      "Hospital Inpatient & Observation Services",
      "Consultations",
      "Emergency Department Services",
      "Nursing Facility & Domiciliary Services",
      "Preventive Medicine & Wellness Visits"
    ],
    "Surgery": [
      "Integumentary System (Skin & Subcutaneous)",
      "Musculoskeletal System (Bones, Joints, Muscles)",
      "Respiratory System (Nose, Lungs, Airway)",
      "Cardiovascular System (Heart & Vessels)",
      "Digestive System (GI & Abdomen)",
      "Urinary & Genital System",
      "Nervous System (Brain, Spine & Nerves)",
      "Eye & Ocular Adnexa"
    ],
    "Radiology": [
      "Diagnostic Radiology (X-Ray)",
      "Diagnostic Ultrasound & Sonography",
      "Radiologic Guidance & Fluoroscopy",
      "Nuclear Medicine & Molecular Imaging",
      "CT Scan & MRI Imaging"
    ],
    "Pathology": [
      "Organ & Disease Specific Panels",
      "Drug Testing & Therapeutic Assays",
      "Evocative / Suppressive Testing",
      "Hematology & Coagulation",
      "Microbiology & Infectious Disease",
      "Anatomic Pathology & Cytopathology"
    ],
    "Medicine": [
      "Immunization Administration & Vaccines",
      "Psychiatry & Behavioral Health",
      "Dialysis & Renal Services",
      "Gastroenterology & Ophthalmology",
      "Cardiovascular & Pulmonary Diagnostic",
      "Neurology & Neuromuscular Procedures"
    ],
    "Physical Therapy": [
      "Physical Therapy Evaluations & Re-evaluations",
      "Therapeutic Procedures & Exercises",
      "Physical Medicine Modalities",
      "Physical Performance Tests & Measurements"
    ],
    "Anesthesia": [
      "Head & Neck Anesthesia",
      "Thorax & Chest Anesthesia",
      "Spine & Spinal Cord Anesthesia",
      "Upper & Lower Abdomen Anesthesia",
      "Lower Extremities Anesthesia"
    ],
    "Diagnostic Cardiology": [
      "Cardiovascular & Pulmonary Diagnostic",
      "EKG & Echocardiogram Services"
    ],
    "General": [
      "Standard Medical Procedures"
    ],
    "Dentistry": [
      "Dental & Oral Procedures"
    ]
  };

  const CPT_CATEGORY_SUBCATEGORIES_MAP: Record<string, string[]> = useMemo(() => {
    const map: Record<string, string[]> = {};
    Object.keys(DEFAULT_CPT_CATEGORY_SUBCATEGORIES_MAP).forEach(cat => {
      map[cat] = [...DEFAULT_CPT_CATEGORY_SUBCATEGORIES_MAP[cat]];
    });

    if (Array.isArray(fetchedCptSubcategories)) {
      fetchedCptSubcategories.forEach((sub: any) => {
        const catName = typeof sub.categoryName === 'string' ? sub.categoryName : (sub.category && sub.category.name);
        const subName = typeof sub === 'string' ? sub : String(sub.name || sub.id || '');
        if (catName && subName) {
          if (!map[catName]) map[catName] = [];
          if (!map[catName].includes(subName)) {
            map[catName].push(subName);
          }
        }
      });
    }
    if (Array.isArray(rawMasterCodes)) {
      rawMasterCodes.forEach((m: any) => {
        const catName = m.category;
        const subName = typeof m.subcategory === 'object' && m.subcategory !== null
          ? (m.subcategory?.name || m.subcategory?.code)
          : String(m.subcategory || m.subCategory || '');
        if (catName && subName && subName.trim()) {
          if (!map[catName]) map[catName] = [];
          if (!map[catName].includes(subName.trim())) {
            map[catName].push(subName.trim());
          }
        }
      });
    }
    return map;
  }, [fetchedCptSubcategories, rawMasterCodes]);

  const createCode = useCreateCptCode();
  const deleteCode = useDeleteCptCode();
  const clearAllCodes = useClearAllCptCodes();
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);

  const { data: bundlesData } = useListBundles({ limit: 1000 });
  const bundlesList = useMemo(() => {
    if (!bundlesData) return [];
    return Array.isArray(bundlesData) ? bundlesData : (bundlesData.items || []);
  }, [bundlesData]);

  const codesInBundles = useMemo(() => {
    const set = new Set<string>();
    if (Array.isArray(bundlesList)) {
      bundlesList.forEach((b: any) => {
        if (b.cptCodes && Array.isArray(b.cptCodes)) {
          b.cptCodes.forEach((c: any) => {
            const codeStr = typeof c === 'string' ? c : (c.code || c.cptCode);
            if (codeStr) set.add(codeStr);
          });
        }
      });
    }
    return Array.from(set).sort();
  }, [bundlesList]);

  const isLoading = codesLoading || masterLoading;

  const providerCodeTypes = user?.provider?.codeTypes || user?.codeTypes || [];
  const selectedCodeTypeNames = useMemo(() => {
    if (!Array.isArray(providerCodeTypes) || providerCodeTypes.length === 0) return [];
    return providerCodeTypes.map((ct: any) => {
      if (!ct) return "";
      let nameStr = "";
      if (typeof ct === "string") {
        nameStr = ct;
        if (rawMasterCodes && Array.isArray(rawMasterCodes)) {
          const foundMaster = rawMasterCodes.find((m: any) => m.codeType?.id === ct || m.codeTypeId === ct || m.codeType?.name === ct);
          if (foundMaster?.codeType?.name) {
            nameStr = foundMaster.codeType.name;
          }
        }
      } else if (typeof ct === "object" && ct !== null) {
        nameStr = ct.name || ct.code || ct.id || "";
      } else {
        nameStr = String(ct || "");
      }
      if (typeof nameStr !== "string") nameStr = String(nameStr || "");
      return nameStr.trim().toUpperCase();
    }).filter(Boolean);
  }, [providerCodeTypes, rawMasterCodes]);

  const availableCodeTypes = useMemo(() => {
    if (selectedCodeTypeNames.length > 0) {
      return selectedCodeTypeNames.map(x => String(x));
    }
    const types = new Set<string>();
    (rawMasterCodes || []).forEach((m: any) => {
      let typeName = "";
      if (typeof m.codeType === "string") {
        typeName = m.codeType;
      } else if (typeof m.codeType === "object" && m.codeType !== null) {
        typeName = m.codeType.name || m.codeType.code || "";
      }
      if (typeName && typeof typeName === "string") {
        types.add(typeName);
      }
    });
    return Array.from(types).sort();
  }, [rawMasterCodes, selectedCodeTypeNames]);

  const normalizeStateCode = (st: string) => {
    if (!st) return "";
    const s = st.trim().toUpperCase();
    if (s === "NEW YORK" || s === "NEW YORK STATE" || s === "N.Y." || s === "NY") return "NY";
    if (s === "CALIFORNIA" || s === "CA") return "CA";
    if (s === "TEXAS" || s === "TX") return "TX";
    if (s === "FLORIDA" || s === "FL") return "FL";
    return s;
  };

  const { data: currentProviderData } = useGetProvider(user?.id || "", { query: { enabled: !!user } });
  const effectiveUser = currentProviderData || user;

  // Build the list of providers from linkedProvidersData or currentProviderData
  const practiceProviders = useMemo(() => {
    if (linkedProvidersData?.allOptions && Array.isArray(linkedProvidersData.allOptions) && linkedProvidersData.allOptions.length > 0) {
      return linkedProvidersData.allOptions.map((p: any) => ({
        id: String(p.providerId || p.id),
        name: p.label || p.name,
        categoryRates: p.categoryRates || {},
        percentage: p.defaultRate ?? (p.medicareCharge ? parseFloat(String(p.medicareCharge)) : undefined),
        isCurrent: Boolean(p.isCurrent)
      }));
    }

    const list: Array<{ id: string; name: string; categoryRates?: Record<string, any>; percentage?: number; isCurrent?: boolean }> = [];
    const mainName = user?.user?.displayName || (user?.firstName ? `${user.firstName} ${user.lastName}` : "Primary Provider");
    const mainId = String(user?.provider?.id || user?.id || "main");
    list.push({ id: mainId, name: `${mainName} (You)`, isCurrent: true });

    const linked = currentProviderData?.linkedProviders || [];
    linked.forEach((lp: any) => {
      const pId = String(lp.providerId || lp.id);
      if (pId && !list.some(p => p.id === pId)) {
        const inv = Array.isArray(teamInvitations?.data)
          ? teamInvitations.data.find((i: any) => i.email === lp.email)
          : Array.isArray(teamInvitations)
          ? teamInvitations.find((i: any) => i.email === lp.email)
          : null;
        list.push({
          id: pId,
          name: lp.name || lp.email || `Provider ${pId}`,
          categoryRates: inv?.categoryRates || {},
          percentage: inv?.percentage ?? (lp.medicareCharge ? parseFloat(String(lp.medicareCharge)) : undefined),
          isCurrent: false
        });
      }
    });
    return list;
  }, [user, currentProviderData, teamInvitations, linkedProvidersData]);

  const hasLinkedProviders = useMemo(() => {
    if (linkedProvidersData?.hasLinkedProviders !== undefined) {
      return Boolean(linkedProvidersData.hasLinkedProviders);
    }
    return practiceProviders.filter(p => !p.isCurrent).length > 0;
  }, [linkedProvidersData, practiceProviders]);

  const providerLocalities = useMemo(() => {
    const selectedLocalityKeys = new Set<string>();

    if (user?.addresses && Array.isArray(user.addresses)) {
      user.addresses.forEach((a: any) => {
        const ids = a.localityIds || (a.localityId ? [a.localityId] : []);
        if (Array.isArray(ids)) {
          ids.forEach((id: any) => {
            if (id) selectedLocalityKeys.add(String(id).trim());
          });
        }
      });
    }

    if (selectedLocalityKeys.size === 0 && user?.localities && Array.isArray(user.localities)) {
      user.localities.forEach((l: any) => {
        if (typeof l === "string") {
          selectedLocalityKeys.add(l.trim());
        } else if (l && typeof l === "object") {
          if (l.id) selectedLocalityKeys.add(String(l.id).trim());
          if (l.localityNumber) selectedLocalityKeys.add(String(l.localityNumber).trim());
          if (l.name) selectedLocalityKeys.add(String(l.name).trim());
        }
      });
    }

    const locMap = new Map<string, any>();
    const targetKeys = Array.from(selectedLocalityKeys);

    if (Array.isArray(localitiesData) && localitiesData.length > 0) {
      if (targetKeys.length > 0) {
        localitiesData.forEach((loc: any) => {
          const locIdStr = String(loc.id).trim();
          const locNumStr = String(loc.localityNumber || '').trim();
          const locNameLower = String(loc.name || '').trim().toLowerCase();

          for (const key of targetKeys) {
            if (!key) continue;
            const keyLower = key.toLowerCase();
            if (locIdStr === key || (locNumStr && locNumStr === key) || locNameLower === keyLower) {
              locMap.set(locIdStr, loc);
              break;
            }
          }
        });
      }

      // Fallback: If no specific practice localities are selected yet, filter by address state
      if (locMap.size === 0) {
        const stateKeys = new Set<string>();
        if (user?.addresses && Array.isArray(user.addresses)) {
          user.addresses.forEach((a: any) => {
            if (a.state) {
              const norm = normalizeStateCode(a.state);
              if (norm) stateKeys.add(norm);
            }
          });
        }
        if (stateKeys.size === 0) stateKeys.add("NY");

        localitiesData.forEach((loc: any) => {
          if (loc.state && stateKeys.has(normalizeStateCode(loc.state))) {
            locMap.set(String(loc.id).trim(), loc);
          }
        });
      }
    }

    return Array.from(locMap.values());
  }, [user, localitiesData]);

  const providerLocations = useMemo(() => {
    const addrList = effectiveUser?.addresses || effectiveUser?.user?.addresses || [];
    const locations: Array<{
      id: string;
      displayLabel: string;
      locality: any;
      localityId?: string;
      raw?: any;
    }> = [];

    if (Array.isArray(addrList) && addrList.length > 0) {
      addrList.forEach((addr: any, index: number) => {
        const street = [addr.buildingNo, addr.street1, addr.street2].filter(Boolean).join(" ");
        const cityStateZip = [addr.city, addr.state, addr.zipCode].filter(Boolean).join(", ");
        const fullAddress = [street, cityStateZip].filter(Boolean).join(" - ") || `Location ${index + 1}`;
        const isPrimaryTag = addr.isPrimary ? " (Primary)" : "";
        const displayLabel = `${fullAddress}${isPrimaryTag}`;

        let targetLocality: any = null;
        const locIds = addr.localityIds || (addr.localityId ? [addr.localityId] : []);

        if (Array.isArray(localitiesData) && localitiesData.length > 0) {
          if (locIds.length > 0) {
            targetLocality = localitiesData.find((l: any) =>
              locIds.some((id: any) => String(l.id) === String(id) || String(l.localityNumber) === String(id))
            );
          }
          if (!targetLocality && addr.state) {
            const normState = normalizeStateCode(addr.state);
            targetLocality = localitiesData.find((l: any) => normalizeStateCode(l.state) === normState);
          }
        }

        locations.push({
          id: addr.id || `location-${index}`,
          displayLabel,
          locality: targetLocality || providerLocalities[0] || null,
          localityId: targetLocality?.id || providerLocalities[0]?.id,
          raw: addr
        });
      });
    }

    if (locations.length === 0 && providerLocalities.length > 0) {
      providerLocalities.forEach((loc: any) => {
        locations.push({
          id: String(loc.id),
          displayLabel: `${loc.name}${loc.state ? `, ${loc.state}` : ''}`,
          locality: loc,
          localityId: String(loc.id),
          raw: loc
        });
      });
    }

    return locations;
  }, [effectiveUser, localitiesData, providerLocalities]);

  const [selectedLocationId, setSelectedLocationId] = useState<string>("all");

  useEffect(() => {
    if (providerLocations.length > 0) {
      const isAlreadySelectedValid = providerLocations.some(l => String(l.id) === String(selectedLocationId));
      if (!selectedLocationId || selectedLocationId === "all" || !isAlreadySelectedValid) {
        setSelectedLocationId(String(providerLocations[0].id));
      }
    }
  }, [providerLocations, selectedLocationId]);

  const selectedLocation = useMemo(() => {
    return providerLocations.find(l => String(l.id) === String(selectedLocationId)) || providerLocations[0] || null;
  }, [selectedLocationId, providerLocations]);

  const selectedLocality = selectedLocation?.locality || providerLocalities[0] || null;

  const getRateForSelectedLocality = (ratesObj: any, selectedLocObj: any, loc: any, codeTypeName: string, codeTypeId: string) => {
    if (!ratesObj || typeof ratesObj !== "object") return null;

    const getValueFromSubObj = (subObj: any): number | null => {
      if (!subObj || typeof subObj !== "object") {
        if (typeof subObj === "number") return subObj;
        if (typeof subObj === "string" && !isNaN(Number(subObj))) return Number(subObj);
        return null;
      }
      if (codeTypeName && subObj[codeTypeName] !== undefined && subObj[codeTypeName] !== null && !isNaN(Number(subObj[codeTypeName]))) {
        return Number(subObj[codeTypeName]);
      }
      if (codeTypeId && subObj[codeTypeId] !== undefined && subObj[codeTypeId] !== null && !isNaN(Number(subObj[codeTypeId]))) {
        return Number(subObj[codeTypeId]);
      }

      const keys = Object.keys(subObj);
      if (codeTypeName) {
        const matchedKey = keys.find(k => k.toUpperCase() === codeTypeName.toUpperCase());
        if (matchedKey && subObj[matchedKey] !== undefined && subObj[matchedKey] !== null && !isNaN(Number(subObj[matchedKey]))) {
          return Number(subObj[matchedKey]);
        }
      }
      if (subObj.default !== undefined && subObj.default !== null && !isNaN(Number(subObj.default))) {
        return Number(subObj.default);
      }
      if (subObj.General !== undefined && subObj.General !== null && !isNaN(Number(subObj.General))) {
        return Number(subObj.General);
      }

      for (const k of keys) {
        const val = Number(subObj[k]);
        if (!isNaN(val) && typeof subObj[k] !== "object" && subObj[k] !== null && subObj[k] !== undefined) return val;
      }
      return null;
    };

    const possibleKeys: string[] = [];

    if (selectedLocObj) {
      if (selectedLocObj.id) possibleKeys.push(String(selectedLocObj.id).trim());
      if (selectedLocObj.raw?.id) possibleKeys.push(String(selectedLocObj.raw.id).trim());

      const locIdx = providerLocations.findIndex(l => String(l.id) === String(selectedLocObj.id));
      if (locIdx !== -1) {
        possibleKeys.push(`loc_${locIdx}`);
        possibleKeys.push(`location-${locIdx}`);
      }

      if (selectedLocObj.raw?.buildingNo || selectedLocObj.raw?.address1 || selectedLocObj.raw?.street1) {
        const fullSt = [selectedLocObj.raw?.buildingNo, selectedLocObj.raw?.address1 || selectedLocObj.raw?.street1].filter(Boolean).join(" ");
        if (fullSt) possibleKeys.push(String(fullSt).trim());
      }

      if (selectedLocObj.raw?.address1) possibleKeys.push(String(selectedLocObj.raw.address1).trim());
      if (selectedLocObj.raw?.street1) possibleKeys.push(String(selectedLocObj.raw.street1).trim());

      if (selectedLocObj.displayLabel) {
        const addrPart = selectedLocObj.displayLabel.split(" - ")[0];
        if (addrPart) possibleKeys.push(String(addrPart).trim());
      }
    }

    if (loc) {
      if (loc.id) possibleKeys.push(String(loc.id).trim());
      if (loc.localityNumber) possibleKeys.push(String(loc.localityNumber).trim());
      if (loc.name && loc.state) possibleKeys.push(`${loc.name} (${loc.state}${loc.city ? ' - ' + loc.city : ''})`);
      if (loc.name && loc.state) possibleKeys.push(`${loc.name} (${loc.state})`);
      if (loc.name) possibleKeys.push(String(loc.name).trim());
    }

    // STEP 1: Check if a location-specific percentage exists for this location
    for (const key of possibleKeys) {
      if (!key) continue;
      const targetRate = ratesObj[key];
      const val = getValueFromSubObj(targetRate);
      if (val !== null) return val;
    }

    for (const key of Object.keys(ratesObj)) {
      const targetRate = ratesObj[key];
      if (!targetRate || typeof targetRate !== "object") continue;

      const keyLower = key.toLowerCase();
      const matchFound = possibleKeys.some(k => k.toLowerCase() === keyLower || keyLower.includes(k.toLowerCase()) || k.toLowerCase().includes(keyLower));

      if (matchFound) {
        const val = getValueFromSubObj(targetRate);
        if (val !== null) return val;
      }
    }

    // STEP 2: Fallback to global default percentage rate in ratesObj
    const globalVal = getValueFromSubObj(ratesObj);
    if (globalVal !== null) return globalVal;

    return null;
  };

  const currentLocationDefaultRate = useMemo(() => {
    if (selectedProviderId && selectedProviderId !== "all") {
      const selProv = practiceProviders.find(p => p.id === selectedProviderId);
      if (selProv?.percentage !== undefined && !isNaN(selProv.percentage)) {
        return selProv.percentage;
      }
    }
    if (codesData?.provider?.defaultRate !== undefined && !isNaN(codesData.provider.defaultRate)) {
      return codesData.provider.defaultRate;
    }
    const ratesObj = effectiveUser?.provider?.categoryRates || effectiveUser?.categoryRates || user?.provider?.categoryRates || user?.categoryRates;
    if (ratesObj && typeof ratesObj === "object") {
      const found = getRateForSelectedLocality(ratesObj, selectedLocation, selectedLocality, "CLFS", "");
      if (found !== null && !isNaN(found)) return found;
    }
    return providerMedicareRate || 100;
  }, [effectiveUser, user, selectedLocation, selectedLocality, providerMedicareRate, selectedProviderId, practiceProviders, codesData]);

  const enrichedMasterCodes = useMemo(() => {
    const pwGpci = selectedLocality?.pwGpci ?? 1.0;
    const peGpci = selectedLocality?.peGpci ?? 1.0;
    const mpGpci = selectedLocality?.mpGpci ?? 1.0;

    return rawMasterCodes.map((master: any) => {
      const providerOverride = codes?.find((c: any) => c.code === master.code);

      const codeTypeName = (master.codeType?.name || (typeof master.codeType === "string" ? master.codeType : "") || "").trim().toUpperCase();
      const isCLFS = codeTypeName === "CLFS";

      const isCustom = Boolean(providerOverride);

      const workRvu = master.workRvu ?? 0.0;
      const peRvu = master.peRvu ?? 0.0;
      const mpRvu = master.mpRvu ?? 0.0;
      const cf = master.conversionFactor || 33.29;

      let baseStandardPrice = master.standardPrice || 0;

      if (!isCLFS && (workRvu > 0 || peRvu > 0 || mpRvu > 0)) {
        const rvuGpciSum = (workRvu * pwGpci) + (peRvu * peGpci) + (mpRvu * mpGpci);
        if (rvuGpciSum > 0) {
          baseStandardPrice = rvuGpciSum * cf;
        }
      }

      let codeTypePct = providerMedicareRate || 100;
      if (selectedProviderId && selectedProviderId !== "all") {
        const selProv = practiceProviders.find(p => p.id === selectedProviderId);
        if (selProv?.categoryRates && typeof selProv.categoryRates === "object") {
          const locRate = selProv.categoryRates[String(selectedLocationId || "all")] ?? selProv.categoryRates["all"] ?? null;
          const ctRate = locRate?.[codeTypeName] ?? locRate?.[master.category] ?? null;
          if (typeof ctRate === "number" && !isNaN(ctRate)) {
            codeTypePct = ctRate;
          } else if (typeof selProv.percentage === "number" && !isNaN(selProv.percentage)) {
            codeTypePct = selProv.percentage;
          }
        } else if (typeof selProv?.percentage === "number" && !isNaN(selProv.percentage)) {
          codeTypePct = selProv.percentage;
        }
      } else {
        const ratesObj = effectiveUser?.provider?.categoryRates || effectiveUser?.categoryRates || user?.provider?.categoryRates || user?.categoryRates;

        if (ratesObj && typeof ratesObj === "object") {
          const foundRate = getRateForSelectedLocality(ratesObj, selectedLocation, selectedLocality, codeTypeName, master.codeTypeId);
          if (foundRate !== null && !isNaN(foundRate)) {
            codeTypePct = foundRate;
          }
        }
      }

      let currentPct = codeTypePct;
      let calculatedPrice = 0;

      if (providerOverride) {
        let locRatesMap: Record<string, { pct: string; price: string }> | null = null;
        let locStatusesMap: Record<string, string> | null = null;
        let isExplicitCustomOverride = false;
        const notesStr = providerOverride.notes;
        if (notesStr) {
          try {
            const parsed = JSON.parse(notesStr);
            if (parsed && typeof parsed.locationRates === "object") {
              locRatesMap = parsed.locationRates;
            }
            if (parsed && typeof parsed.locationStatuses === "object") {
              locStatusesMap = parsed.locationStatuses;
            }
            if (parsed && (parsed.isCustomOverride || parsed.isCustom)) {
              isExplicitCustomOverride = true;
            }
          } catch (e) {}
        }

        const activeLocId = String(selectedLocationId || "all");
        let matchedRate: { pct: string; price: string } | null = null;

        if (locRatesMap) {
          const provKey = selectedProviderId && selectedProviderId !== "all" ? selectedProviderId : "all";
          if (locRatesMap[`${activeLocId}_${provKey}`]) {
            matchedRate = locRatesMap[`${activeLocId}_${provKey}`];
          } else if (locRatesMap[`all_${provKey}`]) {
            matchedRate = locRatesMap[`all_${provKey}`];
          } else if (locRatesMap[`${activeLocId}_all`]) {
            matchedRate = locRatesMap[`${activeLocId}_all`];
          } else if (locRatesMap["all_all"]) {
            matchedRate = locRatesMap["all_all"];
          }
          if (!matchedRate) {
            for (const k of Object.keys(locRatesMap)) {
              if (k.startsWith(`${activeLocId}_`)) {
                matchedRate = locRatesMap[k];
                break;
              }
            }
          }
        }

        if (matchedRate) {
          if (matchedRate.pct) currentPct = parseFloat(matchedRate.pct);
          if (matchedRate.price) calculatedPrice = parseFloat(matchedRate.price);
        } else if (isExplicitCustomOverride && providerOverride.price != null && baseStandardPrice > 0) {
          calculatedPrice = providerOverride.price;
          const customPct = Math.round((providerOverride.price / baseStandardPrice) * 100);
          if (customPct > 0) {
            currentPct = customPct;
          }
        }

        // Resolve location-specific status: prefer locationStatuses[activeLocId], fall back to global status
        const locationStatus = locStatusesMap
          ? (locStatusesMap[activeLocId] || locStatusesMap["all"] || null)
          : null;
        const resolvedStatus = locationStatus || providerOverride.status || "active";

        if (!calculatedPrice) {
          calculatedPrice = Math.round((baseStandardPrice * (currentPct / 100)) * 100) / 100;
        }

        return {
          ...master,
          isCustom,
          standardPrice: baseStandardPrice,
          price: calculatedPrice,
          currentPct,
          status: resolvedStatus,
          providerOverrideId: providerOverride?.id,
          providerOverride: providerOverride,
          notes: providerOverride?.notes,
        };
      }

      if (!calculatedPrice) {
        calculatedPrice = Math.round((baseStandardPrice * (currentPct / 100)) * 100) / 100;
      }

      return {
        ...master,
        isCustom,
        standardPrice: baseStandardPrice,
        price: calculatedPrice,
        currentPct,
        status: "active",
        providerOverrideId: undefined,
        providerOverride: undefined,
        notes: undefined,
      };
    });
  }, [rawMasterCodes, codes, providerMedicareRate, selectedLocality, user, selectedLocationId, selectedProviderId, practiceProviders, effectiveUser]);

  const filteredCustomCodes = useMemo(() => {
    let filtered = enrichedMasterCodes.filter((m: any) => m.isCustom);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      const rangeMatch = s.match(/^(\d{3,6})\s*(?:-|\.\.|to)\s*(\d{3,6})$/i);
      if (rangeMatch) {
        const minVal = parseInt(rangeMatch[1], 10);
        const maxVal = parseInt(rangeMatch[2], 10);
        if (!isNaN(minVal) && !isNaN(maxVal)) {
          filtered = filtered.filter((m: any) => {
            const c = parseInt(m.code, 10);
            return !isNaN(c) && c >= minVal && c <= maxVal;
          });
        }
      } else {
        filtered = filtered.filter((m: any) => m.code.toLowerCase().includes(s) || (m.codeShortDesc || m.description || "").toLowerCase().includes(s));
      }
    }
    if (category && category !== "all") {
      filtered = filtered.filter((m: any) => m.category === category);
    }
    if (subcategory && subcategory !== "all") {
      filtered = filtered.filter((m: any) => {
        const sub = typeof m.subcategory === 'object' && m.subcategory !== null
          ? (m.subcategory?.name || m.subcategory?.code)
          : String(m.subcategory || m.subCategory || "");
        return sub.toLowerCase().includes(subcategory.toLowerCase()) || subcategory.toLowerCase().includes(sub.toLowerCase());
      });
    }
    if (codeType && codeType !== "all") {
      filtered = filtered.filter((m: any) => {
        const typeName = m.codeType?.name || (typeof m.codeType === "string" ? m.codeType : null);
        return typeName === codeType;
      });
    }
    if (statusFilter && statusFilter !== "all") {
      filtered = filtered.filter((m: any) => (m.status || "active") === statusFilter);
    }
    return filtered;
  }, [enrichedMasterCodes, search, category, subcategory, codeType, statusFilter]);

  const mainViewAvailableSubcategories = useMemo(() => {
    const set = new Set<string>();
    if (category && category !== "all" && CPT_CATEGORY_SUBCATEGORIES_MAP[category]) {
      CPT_CATEGORY_SUBCATEGORIES_MAP[category].forEach(s => set.add(s));
    } else {
      Object.values(CPT_CATEGORY_SUBCATEGORIES_MAP).flat().forEach(s => set.add(s));
    }
    enrichedMasterCodes.forEach((m: any) => {
      if (category && category !== "all" && m.category !== category) return;
      const sub = typeof m.subcategory === 'object' && m.subcategory !== null
        ? (m.subcategory?.name || m.subcategory?.code)
        : String(m.subcategory || m.subCategory || "");
      if (sub && sub.trim()) set.add(sub.trim());
    });
    return Array.from(set).sort();
  }, [enrichedMasterCodes, category, CPT_CATEGORY_SUBCATEGORIES_MAP]);

  const customTotalPages = Math.ceil(filteredCustomCodes.length / itemsPerPage) || 1;
  const paginatedCustomCodes = filteredCustomCodes.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Modal View: Filter master codes based on modal search (including range like 99200-99299), category, codeType, subcategory
  const filteredModalCodes = useMemo(() => {
    let filtered = enrichedMasterCodes;

    if (modalSearch.trim()) {
      const s = modalSearch.trim().toLowerCase();
      // Check if search contains a range format like "99200-99299", "99200 - 99299", "99200..99299", "99200 to 99299"
      const rangeMatch = s.match(/^(\d{3,6})\s*(?:-|\.\.|to)\s*(\d{3,6})$/i);
      if (rangeMatch) {
        const minVal = parseInt(rangeMatch[1], 10);
        const maxVal = parseInt(rangeMatch[2], 10);
        if (!isNaN(minVal) && !isNaN(maxVal)) {
          filtered = filtered.filter((m: any) => {
            const c = parseInt(m.code, 10);
            return !isNaN(c) && c >= minVal && c <= maxVal;
          });
        }
      } else {
        filtered = filtered.filter((m: any) =>
          m.code.toLowerCase().includes(s) ||
          (m.codeShortDesc || m.description || "").toLowerCase().includes(s)
        );
      }
    }

    if (modalCategory && modalCategory !== "all") {
      filtered = filtered.filter((m: any) => m.category === modalCategory);
    }

    if (modalCodeType && modalCodeType !== "all") {
      filtered = filtered.filter((m: any) => {
        const typeName = m.codeType?.name || (typeof m.codeType === "string" ? m.codeType : null);
        return typeName === modalCodeType;
      });
    }

    if (modalSubcategory && modalSubcategory !== "all") {
      filtered = filtered.filter((m: any) => {
        const sub = typeof m.subcategory === 'object' && m.subcategory !== null
          ? (m.subcategory?.name || m.subcategory?.code)
          : String(m.subcategory || m.subCategory || "");
        return sub.toLowerCase().includes(modalSubcategory.toLowerCase()) || modalSubcategory.toLowerCase().includes(sub.toLowerCase());
      });
    }

    return filtered;
  }, [enrichedMasterCodes, modalSearch, modalCategory, modalCodeType, modalSubcategory]);

  const availableSubcategories = useMemo(() => {
    const set = new Set<string>();
    if (modalCategory && modalCategory !== "all" && CPT_CATEGORY_SUBCATEGORIES_MAP[modalCategory]) {
      CPT_CATEGORY_SUBCATEGORIES_MAP[modalCategory].forEach(s => set.add(s));
    } else {
      Object.values(CPT_CATEGORY_SUBCATEGORIES_MAP).flat().forEach(s => set.add(s));
    }
    enrichedMasterCodes.forEach((m: any) => {
      if (modalCategory && modalCategory !== "all" && m.category !== modalCategory) {
        return;
      }
      const sub = typeof m.subcategory === 'object' && m.subcategory !== null
        ? (m.subcategory?.name || m.subcategory?.code)
        : String(m.subcategory || m.subCategory || "");
      if (sub && sub.trim()) set.add(sub.trim());
    });
    return Array.from(set).sort();
  }, [enrichedMasterCodes, modalCategory, CPT_CATEGORY_SUBCATEGORIES_MAP]);

  const modalTotalPages = Math.ceil(filteredModalCodes.length / MODAL_ITEMS_PER_PAGE) || 1;
  const paginatedModalCodes = filteredModalCodes.slice((modalPage - 1) * MODAL_ITEMS_PER_PAGE, modalPage * MODAL_ITEMS_PER_PAGE);

  const customCodesCount = codes?.length || 0;

  const handleOpenAddModal = () => {
    setIsAddMode(true);
    handleResetModal();
    if (location !== "/provider/cpt-codes/add") {
      setLocation("/provider/cpt-codes/add");
    }
  };

  const handleCloseAddMode = () => {
    setIsAddMode(false);
    if (location === "/provider/cpt-codes/add") {
      setLocation("/provider/cpt-codes");
    }
  };

  const handleResetModal = () => {
    setModalSearch("");
    setModalCategory("all");
    setModalCodeType("all");
    setModalSubcategory("all");
    setSelectedModalCodes(new Set());
    setIndividualPercentages({});
    setManuallyEditedCodes(new Set());
    setBatchPercentage(providerMedicareRate.toString());
    setModalPage(1);
  };

  const toggleSelectCode = (id: string) => {
    setSelectedModalCodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const validCurrentPageCodes = paginatedModalCodes.filter((m: any) => !m.isCustom);
    const allSelectedOnPage = validCurrentPageCodes.every((m: any) => selectedModalCodes.has(m.id));

    setSelectedModalCodes(prev => {
      const next = new Set(prev);
      if (allSelectedOnPage) {
        // Deselect all on current page
        validCurrentPageCodes.forEach((m: any) => next.delete(m.id));
      } else {
        // Select all on current page
        validCurrentPageCodes.forEach((m: any) => next.add(m.id));
      }
      return next;
    });
  };

  const handleBulkAdd = async () => {
    setIsSubmittingBulk(true);
    const codesToAdd = enrichedMasterCodes.filter((m: any) => selectedModalCodes.has(m.id) && !m.isCustom);

    try {
      const promises = codesToAdd.map((master: any) => {
        const pctStr = individualPercentages[master.id] || batchPercentage;
        const pct = parseFloat(pctStr) || 0;
        const newPrice = Math.round((master.standardPrice * (pct / 100)) * 100) / 100;
        return createCode.mutateAsync({
          data: {
            code: master.code,
            description: master.description,
            category: master.category,
            price: newPrice,
            ...(selectedProviderId && selectedProviderId !== "all" ? { providerId: selectedProviderId } : {})
          }
        });
      });

      await Promise.all(promises);
      queryClient.invalidateQueries({ queryKey: getListCptCodesQueryKey() });
      toast({ title: `Successfully added ${codesToAdd.length} CPT code(s)` });
      handleCloseAddMode();
      handleResetModal();
    } catch (error) {
      toast({ title: "Error adding codes", variant: "destructive" });
    } finally {
      setIsSubmittingBulk(false);
    }
  };

  const handleEditSave = async () => {
    if (!editData) return;
    const { master, provider } = editData;
    const newPrice = parseFloat(editPrice) || Math.round((master.standardPrice * (parseFloat(editPercentage) / 100)) * 100) / 100;
    const overrideId = provider?.id || master.providerOverrideId || master.providerOverride?.id;

    const currentKey = `${editLocationId}_${editProviderId}`;
    const finalRates = {
      ...editLocationRates,
      [currentKey]: { pct: editPercentage, price: editPrice }
    };

    // Preserve existing locationStatuses and update only the currently-selected location's status
    let existingNotesObj: any = {};
    const existingNotesStr = editData?.provider?.notes || editData?.master?.notes;
    if (existingNotesStr) {
      try { existingNotesObj = JSON.parse(existingNotesStr); } catch (e) {}
    }
    const activeLocId = String(editLocationId || selectedLocationId || "all");
    const updatedLocationStatuses: Record<string, string> = {
      ...(existingNotesObj.locationStatuses || {}),
      [activeLocId]: editStatus,
    };
    const notesPayload = JSON.stringify({ ...existingNotesObj, locationRates: finalRates, locationStatuses: updatedLocationStatuses });

    try {
      if (overrideId) {
        await updateCptCode.mutateAsync({
          id: overrideId,
          data: { price: newPrice, notes: notesPayload }
        });
        toast({ title: "CPT Code updated successfully" });
      } else {
        await createCode.mutateAsync({
          data: {
            code: master.code,
            description: master.description,
            category: typeof master.category === 'object' && master.category !== null ? ((master.category as any).name || (master.category as any).code || "") : String(master.category || ""),
            price: newPrice,
            notes: notesPayload
          }
        });
        toast({ title: "CPT Code added successfully" });
      }
      queryClient.invalidateQueries({ queryKey: getListCptCodesQueryKey() });
      setEditData(null);
    } catch (err: any) {
      toast({
        title: "Error updating CPT code",
        description: err.message || "Failed to update CPT code",
        variant: "destructive"
      });
    }
  };

  const handleDelete = () => {
    if (!deleteConfirmData) return;
    const { id, code } = deleteConfirmData;
    deleteCode.mutate({ id }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListCptCodesQueryKey() });
        toast({ title: "Removed from list", description: `Code ${code} has been removed from your customized codes.` });
        setDeleteConfirmData(null);
      },
      onError: (err: any) => {
        toast({ title: "Cannot Delete CPT Code", description: err.message || "Failed to delete code", variant: "destructive" });
        setDeleteConfirmData(null);
      }
    });
  };

  const updateCptCode = useUpdateCptCode();

  const handleToggleStatus = async (master: any, currentStatus: string) => {
    if (!master.providerOverrideId) return;
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    const activeLocId = String(selectedLocationId || "all");
    try {
      // Parse existing notes to preserve locationRates, and update only this location's status
      let existingNotes: any = {};
      if (master.notes) {
        try { existingNotes = JSON.parse(master.notes); } catch (e) {}
      }
      const updatedStatuses: Record<string, string> = {
        ...(existingNotes.locationStatuses || {}),
        [activeLocId]: newStatus,
      };
      const updatedNotes = JSON.stringify({
        ...existingNotes,
        locationStatuses: updatedStatuses,
      });
      await updateCptCode.mutateAsync({
        id: master.providerOverrideId,
        data: { notes: updatedNotes }
      });
      queryClient.invalidateQueries({ queryKey: getListCptCodesQueryKey() });
      toast({
        title: `Code Marked ${newStatus === "active" ? "Active" : "Inactive"}`,
        description: `CPT Code ${master.code} is now ${newStatus} for this location.`
      });
    } catch (err: any) {
      toast({ title: "Failed to update status", description: err.message || "Could not update status", variant: "destructive" });
    }
  };

  const handleClearAll = () => {
    clearAllCodes.mutate(selectedProviderId && selectedProviderId !== "all" ? { providerId: selectedProviderId } : undefined, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListCptCodesQueryKey() });
        toast({ title: "CPT codes reset to 0", description: "All customized CPT codes have been cleared." });
        setIsClearConfirmOpen(false);
      },
      onError: (err: any) => {
        toast({ title: "Cannot Clear All CPT Codes", description: err.message || "Error clearing CPT codes", variant: "destructive" });
        setIsClearConfirmOpen(false);
      }
    });
  };

  const openEdit = (master: any, provider: any) => {
    const override = provider || master.providerOverride || (master.providerOverrideId ? { id: master.providerOverrideId, price: master.price, notes: master.notes } : null);
    let currentPct = master.currentPct || providerMedicareRate || 100;
    let priceVal = master.price || (master.standardPrice || 0) * (currentPct / 100);

    let initialRates: Record<string, { pct: string; price: string }> = {};
    let isExplicitCustomOverride = false;
    const notesStr = override?.notes || master.notes || master.providerOverride?.notes;
    if (notesStr) {
      try {
        const parsed = JSON.parse(notesStr);
        if (parsed && typeof parsed.locationRates === "object") {
          initialRates = parsed.locationRates;
        }
        if (parsed && (parsed.isCustomOverride || parsed.isCustom)) {
          isExplicitCustomOverride = true;
        }
      } catch (e) {}
    }

    const initialLocId = String(selectedLocationId || "all");
    const initialProvId = "all";
    const key = `${initialLocId}_${initialProvId}`;
    const existingRate = initialRates[key] || initialRates[`${initialLocId}_all`] || initialRates["all_all"];

    if (existingRate) {
      if (existingRate.pct) currentPct = parseFloat(existingRate.pct);
      if (existingRate.price) priceVal = parseFloat(existingRate.price);
    } else if (isExplicitCustomOverride && override && override.price != null) {
      priceVal = override.price;
      if (master.standardPrice > 0) {
        currentPct = Math.round((override.price / master.standardPrice) * 100);
      }
    }

    // Read location-specific status from locationStatuses in notes, fall back to global status
    let locationStatuses: Record<string, string> = {};
    if (notesStr) {
      try {
        const parsed = JSON.parse(notesStr);
        if (parsed && typeof parsed.locationStatuses === "object") {
          locationStatuses = parsed.locationStatuses;
        }
      } catch (e) {}
    }
    const currentStatus = locationStatuses[initialLocId] || locationStatuses["all"] || override?.status || master.status || "active";
    setEditLocationRates(initialRates);
    setEditLocationId(initialLocId);
    setEditProviderId(initialProvId);
    setEditStatus(currentStatus);
    setEditPercentage(currentPct.toString());
    setEditPrice(priceVal.toFixed(2));
    setEditData({ master, provider: override });
  };

  if (showAddPage) {
    return (
      <ProviderLayout>
        <div className="w-full pt-4 sm:pt-6 lg:pt-8 flex flex-col min-h-[calc(100vh-8rem)]">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="icon"
                onClick={handleCloseAddMode}
                className="rounded-xl h-10 w-10 shrink-0"
                title="Back to Billing Codes"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Add Billing Codes to Practice</h1>
                <p className="text-muted-foreground text-sm mt-0.5">Browse master billing codes, select codes for your practice, and configure pricing.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleResetModal} className="text-muted-foreground h-9 px-3 text-xs">
                Reset Filters
              </Button>
              <Button variant="outline" onClick={handleCloseAddMode} className="rounded-xl h-9 px-4 text-xs font-semibold">
                Cancel & Back
              </Button>
            </div>
          </div>

          {/* Filters Bar */}
          <div className="bg-card border border-border p-4 rounded-2xl shadow-sm mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={modalSearch}
                  onChange={e => { setModalSearch(e.target.value); setModalPage(1); }}
                  placeholder="Search code, desc, or range (e.g. 99200-99299)..."
                  className="pl-10 rounded-xl"
                  title="Search code, description, or range like 99200-99299"
                />
              </div>
              <Select value={modalCodeType} onValueChange={(val) => { setModalCodeType(val); setModalPage(1); }}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="All Code Types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Code Types</SelectItem>
                  {availableCodeTypes.map(ct => <SelectItem key={ct} value={ct}>{ct}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={modalCategory} onValueChange={(val) => { setModalCategory(val); setModalSubcategory("all"); setModalPage(1); }}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="All Categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={modalSubcategory} onValueChange={(val) => { setModalSubcategory(val); setModalPage(1); }}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="All Subcategories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subcategories</SelectItem>
                  {availableSubcategories.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Main Full Page Split Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 items-start">
            {/* Left Side: CPT Codes List (2 cols) */}
            <div className="lg:col-span-2 border border-border rounded-2xl flex flex-col bg-card shadow-sm overflow-hidden min-h-[500px]">
              <div className="bg-muted/50 p-4 border-b border-border flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 w-4.5 h-4.5 text-primary cursor-pointer"
                    checked={
                      paginatedModalCodes.filter((m: any) => !m.isCustom).length > 0 &&
                      paginatedModalCodes.filter((m: any) => !m.isCustom).every((m: any) => selectedModalCodes.has(m.id))
                    }
                    onChange={toggleSelectAll}
                  />
                  <span className="text-sm font-semibold">Select All on Page</span>
                </div>
                <span className="text-xs text-muted-foreground font-medium">
                  Showing {filteredModalCodes.length} matching master codes
                </span>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-2.5 max-h-[600px]">
                {paginatedModalCodes.length === 0 ? (
                  <div className="py-16 text-center text-sm text-muted-foreground space-y-2">
                    <FileText className="w-10 h-10 mx-auto opacity-20" />
                    <p className="font-medium">No matching master codes found.</p>
                    <p className="text-xs">Try adjusting your filters or search terms.</p>
                  </div>
                ) : paginatedModalCodes.map((master: any) => {
                  const isSelected = selectedModalCodes.has(master.id);
                  const isDisabled = master.isCustom || isSelected;
                  return (
                    <div
                      key={master.id}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${isDisabled
                        ? "opacity-60 bg-muted/30 border-border cursor-not-allowed"
                        : isSelected
                          ? "border-primary bg-primary/5 shadow-sm cursor-pointer"
                          : "border-border hover:border-primary/40 cursor-pointer bg-background hover:bg-slate-50 dark:hover:bg-slate-900"
                        }`}
                      onClick={() => !isDisabled && toggleSelectCode(master.id)}
                    >
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 w-4 h-4 text-primary cursor-pointer shrink-0"
                        checked={master.isCustom || isSelected}
                        disabled={isDisabled}
                        readOnly
                      />
                      <span className="font-mono font-bold text-sm text-primary bg-primary/10 px-2.5 py-1 rounded-md shrink-0">
                        {master.code}
                      </span>
                      <Badge variant="outline" className="text-[11px] bg-blue-50 text-blue-700 border-blue-200 shrink-0">
                        {typeof master.codeType === 'object' && master.codeType !== null
                          ? ((master.codeType as any)?.name || (master.codeType as any)?.code || "CPT")
                          : String(master.codeType || "CPT")}
                      </Badge>
                      <Badge variant="secondary" className="text-[11px] shrink-0">
                        {typeof master.category === 'object' && master.category !== null
                          ? ((master.category as any)?.name || (master.category as any)?.code || "")
                          : String(master.category || "")}
                      </Badge>
                      <span className="text-xs text-foreground/80 truncate flex-1 font-medium" title={master.description}>
                        {master.description}
                      </span>
                      {master.isCustom && <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200 shrink-0">Already Added</Badge>}
                    </div>
                  );
                })}
              </div>

              {modalTotalPages > 1 && (
                <div className="p-3 border-t border-border flex justify-center bg-muted/30 shrink-0">
                  <ListPagination
                    currentPage={modalPage}
                    totalPages={modalTotalPages}
                    onPageChange={setModalPage}
                  />
                </div>
              )}
            </div>

            {/* Right Side: Selected Codes Panel (1 col) */}
            <div className="border border-border rounded-2xl bg-card shadow-sm flex flex-col overflow-hidden min-h-[500px]">
              <div className="bg-muted/50 p-4 border-b border-border shrink-0 flex justify-between items-center">
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">Selected Codes</h3>
                <Badge variant={selectedModalCodes.size > 0 ? "default" : "secondary"}>
                  {selectedModalCodes.size} Selected
                </Badge>
              </div>

              <div className="flex-1 overflow-y-auto p-4 content-start flex flex-wrap gap-2 max-h-[520px]">
                {selectedModalCodes.size === 0 ? (
                  <div className="w-full h-full min-h-[300px] flex flex-col items-center justify-center text-muted-foreground p-6 text-center space-y-3">
                    <FileText className="w-12 h-12 opacity-20" />
                    <p className="text-sm font-medium">No CPT codes selected yet.</p>
                    <p className="text-xs text-slate-400">Select codes from the master list to add them to your practice catalog.</p>
                  </div>
                ) : (
                  Array.from(selectedModalCodes).map(id => {
                    const master = enrichedMasterCodes.find((m: any) => m.id === id);
                    if (!master) return null;
                    return (
                      <div key={id} className="flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-xl text-sm font-medium border border-primary/20 shadow-sm">
                        <span className="font-mono font-bold">{master.code}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelectCode(id);
                          }}
                          className="text-primary/70 hover:text-primary hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Bottom Actions inside Panel */}
              <div className="p-4 border-t border-border bg-muted/30 space-y-3 shrink-0">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Selected Total:</span>
                  <span className="font-bold text-foreground">{selectedModalCodes.size} CPT Code(s)</span>
                </div>
                <Button
                  onClick={handleBulkAdd}
                  disabled={selectedModalCodes.size === 0 || isSubmittingBulk}
                  className="w-full rounded-xl h-11 font-bold text-sm shadow-md shadow-primary/20"
                >
                  {isSubmittingBulk ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Adding Codes...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Check className="w-4 h-4" /> Add Selected Codes ({selectedModalCodes.size})
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <div className="w-full pt-4 sm:pt-6 lg:pt-8 flex flex-col min-h-[calc(100vh-8rem)]">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-xl flex-shrink-0">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Billing Codes</h1>
              <p className="text-muted-foreground text-sm mt-1">
                Your default rate is <span className="font-semibold">{currentLocationDefaultRate}%</span> of Fee Schedule.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {customCodesCount > 0 && (
              <Button
                variant="outline"
                onClick={() => setIsClearConfirmOpen(true)}
                className="gap-2 rounded-xl h-11 px-4 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/20"
              >
                <Trash2 className="w-4 h-4" />
                Clear All
              </Button>
            )}
            <Button onClick={handleOpenAddModal} className="gap-2 rounded-xl h-11 px-6">
              <Plus className="w-4 h-4" />
              Add CPT Codes
            </Button>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="flex flex-col gap-2.5 sm:gap-3 mb-5">
          {/* Top Row: Search + Provider Filter (if linked) + Location Filter */}
          <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 items-stretch sm:items-center">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                placeholder="Search your CPT codes..."
                className="pl-10 rounded-xl h-10 text-xs sm:text-sm"
              />
            </div>

            {/* Provider Filter Dropdown */}
            {hasLinkedProviders && (
              <div className="w-full sm:w-60 lg:w-64 shrink-0">
                <Select value={selectedProviderId} onValueChange={(val) => { setSelectedProviderId(val); setCurrentPage(1); }}>
                  <SelectTrigger className="rounded-xl w-full h-10 text-xs sm:text-sm">
                    <div className="flex items-center gap-2 truncate">
                      <Users className="w-4 h-4 text-primary shrink-0" />
                      <SelectValue placeholder="All Providers" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <span>All Providers</span>
                    </SelectItem>
                    {practiceProviders.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        <span>{p.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Location Filter Dropdown */}
            <div className="w-full sm:w-60 lg:w-64 shrink-0">
              <Select value={selectedLocationId} onValueChange={(val) => { setSelectedLocationId(val); setCurrentPage(1); }}>
                <SelectTrigger className="rounded-xl w-full h-10 text-xs sm:text-sm">
                  <div className="flex items-center gap-2 truncate">
                    <MapPin className="w-4 h-4 text-primary shrink-0" />
                    <SelectValue placeholder="Filter by Location" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {providerLocations.map((loc: any) => (
                    <SelectItem key={loc.id} value={String(loc.id)}>
                      <span>{loc.displayLabel}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Bottom Row: Category, Subcategory, Code Type, Status */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3 w-full">
            <div>
              <Select value={category} onValueChange={(val) => { setCategory(val); setSubcategory("all"); setCurrentPage(1); }}>
                <SelectTrigger className="rounded-xl w-full h-10 text-xs sm:text-sm"><SelectValue placeholder="All Categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Select value={subcategory} onValueChange={(val) => { setSubcategory(val); setCurrentPage(1); }}>
                <SelectTrigger className="rounded-xl w-full h-10 text-xs sm:text-sm"><SelectValue placeholder="All Subcategories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subcategories</SelectItem>
                  {mainViewAvailableSubcategories.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Select value={codeType} onValueChange={(val) => { setCodeType(val); setCurrentPage(1); }}>
                <SelectTrigger className="rounded-xl w-full h-10 text-xs sm:text-sm">
                  <SelectValue placeholder="All Code Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Code Types</SelectItem>
                  {availableCodeTypes.map((ct: string) => (
                    <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setCurrentPage(1); }}>
                <SelectTrigger className="rounded-xl w-full h-10 text-xs sm:text-sm">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active Only</SelectItem>
                  <SelectItem value="inactive">Inactive Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-2xl border border-border shadow-xs overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="border-b border-border bg-muted/60 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  <th className="px-4 py-3.5 whitespace-nowrap">Code Type</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">Billing Code</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">Code Category</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">Sub Category</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">Description</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">Base Rate</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">% Percentage</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">Billable Rate</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">Status</th>
                  <th className="px-4 py-3.5 text-right whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {isLoading ? Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}><td colSpan={10} className="px-4 py-3.5"><Skeleton className="h-8 w-full" /></td></tr>
                )) : paginatedCustomCodes.length === 0 ? (
                  <tr><td colSpan={10} className="py-16 text-center text-muted-foreground text-sm">
                    <FileText className="w-10 h-10 mx-auto mb-4 opacity-30 text-primary" />
                    <p className="font-medium mb-1 text-foreground">No Billing Codes selected yet</p>
                    <p className="text-xs text-muted-foreground mb-4">Click '+ Add Billing Codes' to select billing codes for your practice.</p>
                    <Button onClick={handleOpenAddModal} variant="default" size="sm" className="rounded-xl">
                      <Plus className="w-4 h-4 mr-2" /> Add Billing Codes
                    </Button>
                  </td></tr>
                ) : paginatedCustomCodes.map((master: any, i: number) => (
                  <motion.tr
                    key={master.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: (i % 20) * 0.02 }}
                    className={`hover:bg-accent/50 transition-colors cursor-pointer ${master.status === "inactive" ? "bg-slate-50/50 dark:bg-slate-900/40 opacity-75" : "bg-primary/5"}`}
                    onClick={() => openEdit(master, master.providerOverride)}
                  >
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <Badge variant="outline" className="text-xs bg-muted/60 font-medium">
                        {master.codeType?.name || (typeof master.codeType === "string" ? master.codeType : "CPT")}
                      </Badge>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className={`font-mono font-bold ${master.status === "inactive" ? "text-slate-500" : "text-primary"}`}>{master.code}</span>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <Badge variant="secondary" className="text-xs font-semibold">
                        {typeof master.category === 'object' && master.category !== null
                          ? ((master.category as any)?.name || (master.category as any)?.code || "")
                          : String(master.category || "")}
                      </Badge>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className="text-xs text-muted-foreground font-medium truncate max-w-[180px] block">
                        {typeof master.subcategory === 'object' && master.subcategory !== null
                          ? (master.subcategory?.name || master.subcategory?.code || "")
                          : String(master.subcategory || master.subCategory || "—")}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm max-w-xs">
                      <p className="truncate" title={master.description || master.codeShortDesc}>{master.description || master.codeShortDesc}</p>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-sm text-muted-foreground">
                      ${master.standardPrice.toFixed(2)}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className="font-semibold text-primary">{master.currentPct}%</span>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className="font-bold">${master.price.toFixed(2)}</span>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <Badge
                        variant={master.status === "active" ? "default" : "secondary"}
                        className={master.status === "active"
                          ? "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400 border-green-300 hover:bg-green-200 cursor-pointer text-[11px] px-2.5 py-1 rounded-full font-bold flex items-center gap-1.5 transition-all w-fit"
                          : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-slate-300 hover:bg-slate-200 cursor-pointer text-[11px] px-2.5 py-1 rounded-full font-bold flex items-center gap-1.5 transition-all w-fit"
                        }
                        onClick={() => handleToggleStatus(master, master.status)}
                        title={`Click to switch to ${master.status === "active" ? "Inactive" : "Active"}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${master.status === "active" ? "bg-green-500 animate-pulse" : "bg-slate-400"}`} />
                        {master.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-7 h-7 text-muted-foreground hover:text-primary"
                          onClick={() => openEdit(master, master.providerOverride)}
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-7 h-7 text-muted-foreground hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            const targetCode = master.providerOverride?.code || master.code;
                            const matchingBundles = (bundlesList || []).filter((b: any) => {
                              if (!b.cptCodes || !Array.isArray(b.cptCodes)) return false;
                              return b.cptCodes.some((c: any) => {
                                const codeStr = typeof c === 'string' ? c : (c.code || c.cptCode);
                                return codeStr === targetCode;
                              });
                            }).map((b: any) => b.name || "Service Bundle");

                            setDeleteConfirmData({
                              id: master.providerOverride?.id || master.providerOverrideId,
                              code: targetCode,
                              usedInBundles: matchingBundles
                            });
                          }}
                          title="Remove custom code"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredCustomCodes.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-auto pt-6 mb-8 text-sm text-muted-foreground border-t border-border flex-shrink-0">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span>Rows per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-border rounded-md bg-transparent px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <span>
                Showing {filteredCustomCodes.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredCustomCodes.length)} of {filteredCustomCodes.length}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <div className="flex gap-1">
                {Array.from({ length: customTotalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-8 h-8 rounded-md flex items-center justify-center font-medium ${currentPage === i + 1
                        ? "bg-primary text-primary-foreground font-bold"
                        : "hover:bg-muted"
                      }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(customTotalPages, p + 1))}
                disabled={currentPage === customTotalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Rate & Price Dialog */}
      <Dialog open={!!editData} onOpenChange={(open) => !open && setEditData(null)}>
        <DialogContent className="rounded-2xl max-w-2xl sm:max-w-2xl w-full">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-primary" /> Edit Billing Code Rate
            </DialogTitle>
          </DialogHeader>
          {editData && (
            <div className="space-y-4 mt-2">
              <div className="bg-slate-50 dark:bg-slate-900 p-3 rounded-xl border border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <div>
                  <Badge className="font-mono text-xs px-2 py-0.5">{editData.master.code}</Badge>
                  <p className="text-xs text-muted-foreground mt-1 max-w-[350px] sm:max-w-[450px] truncate">{editData.master.description}</p>
                </div>
                {editData.master.standardPrice > 0 && (
                  <div className="text-right">
                    <span className="text-[10px] text-muted-foreground block font-medium">Fee Schedule Base Rate</span>
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                      ${editData.master.standardPrice.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              {/* Location & Provider Selection */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-muted-foreground">Location</Label>
                  <Select value={editLocationId} onValueChange={handleEditLocationChange}>
                    <SelectTrigger className="rounded-xl mt-1 text-xs font-semibold">
                      <SelectValue placeholder="All Locations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Locations (Group Default)</SelectItem>
                      {providerLocations.map((loc) => (
                        <SelectItem key={loc.id} value={String(loc.id)}>
                          {loc.displayLabel}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-muted-foreground">Provider</Label>
                  <Select value={editProviderId} onValueChange={handleEditProviderChange}>
                    <SelectTrigger className="rounded-xl mt-1 text-xs font-semibold">
                      <SelectValue placeholder="All Providers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Providers (Group Default)</SelectItem>
                      {practiceProviders.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>% Adjusted</Label>
                  <div className="relative mt-1">
                    <Input
                      type="number"
                      value={editPercentage}
                      onChange={e => handleEditPercentageChange(e.target.value)}
                      className="pr-7 rounded-xl font-bold"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-bold">%</span>
                  </div>
                </div>

                <div>
                  <Label>Billable Rate</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editPrice}
                      onChange={e => handleEditPriceChange(e.target.value)}
                      className="pl-7 rounded-xl font-bold text-primary"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="block mb-1.5 text-xs font-semibold text-muted-foreground">Code Status</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant={editStatus === "active" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setEditStatus("active")}
                    className={`rounded-xl px-4 py-1.5 font-bold flex items-center gap-1.5 transition-all ${
                      editStatus === "active" 
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs" 
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <CheckCircle2 className="w-4 h-4 text-emerald-300" /> Active
                  </Button>
                  <Button
                    type="button"
                    variant={editStatus === "inactive" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setEditStatus("inactive")}
                    className={`rounded-xl px-4 py-1.5 font-bold flex items-center gap-1.5 transition-all ${
                      editStatus === "inactive" 
                        ? "bg-rose-600 hover:bg-rose-700 text-white shadow-xs" 
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <X className="w-4 h-4 text-rose-300" /> Inactive
                  </Button>
                </div>
              </div>

              <DialogFooter className="pt-2">
                <Button variant="outline" onClick={() => setEditData(null)} className="rounded-xl">Cancel</Button>
                <Button onClick={handleEditSave} disabled={createCode.isPending || deleteCode.isPending} className="rounded-xl font-bold">
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* SINGLE CODE DELETE / BUNDLE WARNING DIALOG */}
      <Dialog open={!!deleteConfirmData} onOpenChange={(open) => !open && setDeleteConfirmData(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${deleteConfirmData?.usedInBundles && deleteConfirmData.usedInBundles.length > 0 ? "text-destructive font-bold" : ""}`}>
              <AlertTriangle className={`w-5 h-5 ${deleteConfirmData?.usedInBundles && deleteConfirmData.usedInBundles.length > 0 ? "text-destructive" : "text-amber-500"}`} />
              {deleteConfirmData?.usedInBundles && deleteConfirmData.usedInBundles.length > 0 ? "Cannot Delete CPT Code" : "Remove Billing Code"}
            </DialogTitle>
          </DialogHeader>

          <div className="py-2 space-y-3">
            {deleteConfirmData?.usedInBundles && deleteConfirmData.usedInBundles.length > 0 ? (
              <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 space-y-2 text-sm text-destructive dark:text-red-400">
                <p className="font-bold text-xs uppercase tracking-wider flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4 shrink-0 text-destructive" /> Action Blocked
                </p>
                <p className="text-sm text-foreground/90 font-medium leading-relaxed">
                  CPT Code <span className="font-mono font-bold text-destructive">{deleteConfirmData.code}</span> is currently used in the following Service Bundle(s):{" "}
                  <span className="font-semibold underline">{deleteConfirmData.usedInBundles.join(", ")}</span>.
                </p>
                <p className="text-xs text-muted-foreground pt-1">
                  Please remove this CPT code from your Service Bundle(s) before discarding or deleting it.
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground leading-relaxed">
                Are you sure you want to remove <strong>{deleteConfirmData?.code}</strong> from your customized codes?
              </p>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteConfirmData(null)} className="rounded-xl">
              {deleteConfirmData?.usedInBundles && deleteConfirmData.usedInBundles.length > 0 ? "Close" : "Cancel"}
            </Button>
            {(!deleteConfirmData?.usedInBundles || deleteConfirmData.usedInBundles.length === 0) && (
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteCode.isPending}
                className="rounded-xl font-bold"
              >
                {deleteCode.isPending ? "Removing..." : "Remove Code"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EXCEL IMPORT DIALOG */}
      <Dialog open={isExcelModalOpen} onOpenChange={setIsExcelModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-blue-600" />
              Import Practice CPT Codes from Excel
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2 text-sm">
            <p className="text-muted-foreground text-xs">
              Upload an Excel file (.xlsx, .xls, or .csv) to bulk import procedure codes and custom prices into your practice menu.
            </p>

            <div className="border-2 border-dashed border-border rounded-xl p-5 text-center bg-muted/30">
              <input
                type="file"
                id="provider-excel-input"
                accept=".xlsx, .xls, .csv"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setSelectedExcelFile(e.target.files[0]);
                    setExcelSummary(null);
                  }
                }}
              />
              <label htmlFor="provider-excel-input" className="cursor-pointer flex flex-col items-center gap-2">
                <UploadCloud className="w-8 h-8 text-blue-500" />
                <span className="font-semibold text-xs text-foreground">
                  {selectedExcelFile ? selectedExcelFile.name : "Click to select Excel file"}
                </span>
                <span className="text-[11px] text-muted-foreground">.xlsx, .xls, or .csv</span>
              </label>
            </div>

            {excelSummary && (
              <div className="bg-emerald-50/70 border border-emerald-200 rounded-xl p-3 text-xs space-y-1">
                <p className="font-bold text-emerald-800 flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Import Complete
                </p>
                <div className="flex gap-4 text-slate-700 pt-1">
                  <span>Total: <strong>{excelSummary.totalRows}</strong></span>
                  <span>Added: <strong className="text-emerald-700">+{excelSummary.inserted}</strong></span>
                  <span>Updated: <strong className="text-blue-700">{excelSummary.updated}</strong></span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsExcelModalOpen(false)}>Close</Button>
            <Button disabled={!selectedExcelFile || uploadingExcel} onClick={handleExcelUpload} className="gap-2 bg-blue-600 hover:bg-blue-500">
              {uploadingExcel ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
              {uploadingExcel ? "Processing..." : "Upload & Process"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CLEAR ALL CONFIRMATION / BUNDLE WARNING DIALOG */}
      <Dialog open={isClearConfirmOpen} onOpenChange={setIsClearConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${codesInBundles.length > 0 ? "text-destructive font-bold" : ""}`}>
              <AlertTriangle className={`w-5 h-5 ${codesInBundles.length > 0 ? "text-destructive" : "text-amber-500"}`} />
              {codesInBundles.length > 0 ? "Cannot Clear All CPT Codes" : "Clear All CPT Codes?"}
            </DialogTitle>
          </DialogHeader>

          <div className="py-2 space-y-3">
            {codesInBundles.length > 0 ? (
              <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 space-y-2 text-sm text-destructive dark:text-red-400">
                <p className="font-bold text-xs uppercase tracking-wider flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4 shrink-0 text-destructive" /> Action Blocked
                </p>
                <p className="text-sm text-foreground/90 font-medium leading-relaxed">
                  Cannot Clear All CPT Codes: The following CPT code(s) are currently used in Service Bundles:{" "}
                  <span className="font-mono font-bold text-destructive underline">{codesInBundles.join(", ")}</span>.
                </p>
                <p className="text-xs text-muted-foreground pt-1">
                  Please remove them from your Service Bundles before clearing all codes.
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground leading-relaxed">
                Are you sure you want to clear all customized CPT codes? Your CPT code list will be reset to 0 codes.
              </p>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsClearConfirmOpen(false)} className="rounded-xl">
              {codesInBundles.length > 0 ? "Close" : "Cancel"}
            </Button>
            {codesInBundles.length === 0 && (
              <Button
                variant="destructive"
                onClick={handleClearAll}
                disabled={clearAllCodes.isPending}
                className="rounded-xl font-bold"
              >
                {clearAllCodes.isPending ? "Clearing..." : "Clear All"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ProviderLayout>
  );
}
